/*
* === ComingSoonPage.js (JB Marks GenZ Edition - Fully Functional Waitlist) ===
*/
import React, { useState } from 'react';
import { Box, Container, Typography, Button, Stack, Paper, Chip, InputBase, Snackbar, Alert, CircularProgress } from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import HomeIcon from '@mui/icons-material/Home';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useNavigate } from 'react-router-dom';
import { tenantConfig } from '../../config/tenantConfig';
import api from '../../services/api';

const ComingSoonPage = () => {
    const navigate = useNavigate();
    
    // --- WAITLIST STATE ---
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const handleNotify = async () => {
        // Basic Regex Email Validation
        if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
            setSnackbar({ open: true, message: 'Please enter a valid email address.', severity: 'warning' });
            return;
        }

        setLoading(true);
        try {
            // Sends the email and the tenant ID so you know where the lead came from
            await api.post('/public/waitlist', { email, tenant: tenantConfig.id });
            setSnackbar({ open: true, message: 'Success! We will notify you the second we launch.', severity: 'success' });
            setEmail(''); // Clear the input on success
        } catch (error) {
            setSnackbar({ 
                open: true, 
                message: error.response?.data?.message || 'Something went wrong. Please try again later.', 
                severity: 'error' 
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                background: 'linear-gradient(rgba(18, 18, 18, 0.88), rgba(18, 18, 18, 0.98)), url(https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                color: '#fff',
                position: 'relative'
            }}
        >
            {/* --- ADMIN / STAFF LOGIN --- */}
            <Box sx={{ position: 'absolute', top: 24, right: 24, zIndex: 10 }}>
                <Button 
                    onClick={() => navigate('/login')}
                    startIcon={<AdminPanelSettingsIcon />}
                    sx={{ 
                        color: 'rgba(255,255,255,0.5)', 
                        textTransform: 'none',
                        fontWeight: 'bold',
                        '&:hover': { color: '#8b7d53', bgcolor: 'rgba(139, 125, 83, 0.1)' }
                    }}
                >
                    Staff Portal
                </Button>
            </Box>

            <Container maxWidth="md" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 4, zIndex: 2 }}>
                
                {/* --- BRAND LOGO --- */}
                <Box 
                    component="img" 
                    src={tenantConfig.logo || '/assets/jbmarks-logo.png'} 
                    alt="JB Marks Logo" 
                    sx={{ height: 110, mb: 4, objectFit: 'contain', filter: 'drop-shadow(0px 4px 12px rgba(0,0,0,0.6))' }} 
                    onError={(e) => e.target.style.display = 'none'} 
                />

                {/* --- MAIN CARD --- */}
                <Paper 
                    elevation={24} 
                    sx={{ 
                        p: { xs: 4, md: 6 }, 
                        borderRadius: 4, 
                        background: 'rgba(30, 30, 30, 0.6)', 
                        backdropFilter: 'blur(16px)',
                        border: '1px solid rgba(139, 125, 83, 0.3)',
                        color: '#fff',
                        textAlign: 'center',
                        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.5)'
                    }}
                >
                    <Chip icon={<RocketLaunchIcon style={{ color: '#111' }}/>} label="System Upgrade in Progress" sx={{ bgcolor: '#8b7d53', color: '#111', fontWeight: 'bold', mb: 3, px: 1 }} />

                    <Typography variant="h2" fontWeight="900" gutterBottom sx={{ fontSize: { xs: '2.5rem', md: '4rem' }, letterSpacing: '-1px' }}>
                        Your Degree.<br />
                        <span style={{ color: '#8b7d53' }}>Fully Funded.</span>
                    </Typography>
                    
                    <Typography variant="h6" sx={{ mb: 4, opacity: 0.85, fontWeight: 400, maxWidth: '600px', mx: 'auto', lineHeight: 1.6 }}>
                        We are upgrading our application portal to bring you a seamless, mobile-first experience. Built exclusively for NUM members and their dependents.
                    </Typography>

                    <Box sx={{ mb: 5 }}>
                        <Typography variant="overline" sx={{ letterSpacing: 1.5, color: '#8b7d53', fontWeight: 'bold' }}>
                            We provide the bursaries. You provide the dedication.
                        </Typography>
                        <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" justifyContent="center" sx={{ mt: 2 }}>
                            <Chip icon={<SchoolIcon sx={{ color: '#fff !important' }}/>} label="Tuition" variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.2)', bgcolor: 'rgba(0,0,0,0.3)' }} />
                            <Chip icon={<HomeIcon sx={{ color: '#fff !important' }}/>} label="Accommodation" variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.2)', bgcolor: 'rgba(0,0,0,0.3)' }} />
                            <Chip icon={<RestaurantIcon sx={{ color: '#fff !important' }}/>} label="Meals" variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.2)', bgcolor: 'rgba(0,0,0,0.3)' }} />
                            <Chip icon={<MenuBookIcon sx={{ color: '#fff !important' }}/>} label="Textbooks" variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.2)', bgcolor: 'rgba(0,0,0,0.3)' }} />
                        </Stack>
                    </Box>

                    {/* --- EMAIL WAITLIST CTA (Now Functional) --- */}
                    <Box component="form" onSubmit={(e) => { e.preventDefault(); handleNotify(); }} sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        bgcolor: 'rgba(0,0,0,0.5)', 
                        borderRadius: 50, 
                        p: 1, 
                        maxWidth: 450, 
                        mx: 'auto',
                        border: '1px solid rgba(255,255,255,0.1)'
                    }}>
                        <InputBase 
                            placeholder="Drop your email for alerts..." 
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                            sx={{ color: '#fff', ml: 2, flex: 1, fontSize: '0.95rem' }} 
                        />
                        <Button 
                            type="submit"
                            variant="contained" 
                            disabled={loading}
                            sx={{ 
                                bgcolor: '#8b7d53', 
                                color: '#111', 
                                borderRadius: 50, 
                                px: 4, 
                                py: 1.2,
                                fontWeight: 'bold',
                                textTransform: 'none',
                                '&:hover': { bgcolor: '#a39466' }
                            }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Notify Me'}
                        </Button>
                    </Box>

                    <Typography variant="body2" sx={{ mt: 3, opacity: 0.6, fontWeight: 'bold' }}>
                        🗓️ Applications open every March and close in July. Don't sleep on this.
                    </Typography>
                </Paper>
            </Container>

            {/* --- FOOTER --- */}
            <Box component="footer" sx={{ py: 3, textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.05)', bgcolor: 'rgba(0,0,0,0.6)', zIndex: 2 }}>
                <Typography variant="body2" sx={{ opacity: 0.5 }}>
                    &copy; {new Date().getFullYear()} JB Marks Education Trust Fund. Powered by BrightSparc.
                </Typography>
            </Box>

            {/* --- SNACKBAR ALERT --- */}
            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ComingSoonPage;